import { stopPropagationForAgGrid } from '@ag-grid-community/core/dist/es6/utils/event';
import React, { useCallback, useEffect, useRef } from 'react';

const EVENT_TYPE_CLICK = 'click';

/**
 * This is a styleable convenience wrapper for interactive cell renderers
 * embedded into a grid. It provides a way to prevent the grid's onRowClicked
 * handler from getting triggered when a user is interacting with an interactive
 * cell renderer inside this wrapper. One example is rendering a dropdown to
 * change status of complaints in a grid, while at the same time being able to
 * handle clicking anywhere else in the row by opening a property details modal.
 * Without this wrapper, clicking on the dropdown to open it would result in
 * the modal being opened, and the user wouldn't be able to select anything from
 * the dropdown.
 *
 * React seems to handle (almost) all of the events for its components through
 * centralized event handlers bound to the `document` element. Those event handlers
 * would wait for any events on rendered components to bubble up all the way to the
 * `document`, and then React re-dispatches those events, actually invoking the
 * callbacks we usually set through something like the `onClick` prop (and that seems to be
 * the reason why we deal with "synthetic" events when working with React).
 *
 * Ag-Grid, on the other hand, despite being a React component, seems to grab a ref
 * to each row and attach its own event handlers for clicks directly via
 * `addEventListener` directly on each row's `div` element.
 *
 * If we have a custom renderer that uses regular React events inside an ag-grid row,
 * we end up with the following DOM nodes and native event handlers:
 *
 * document        - React's 'click' handler                /\
 * ag-grid's div   - ag-grid's 'click' handler on a row     |  click event bubbles
 * custom renderer - [!] no handler                         |  upwards
 *
 * Intuitively, we'd think that we could simply stop propagation inside
 * the custom renderer. However, remembering that React actually only starts
 * to process events when they bubble up to the document, by the time React
 * gets the click event on the custom renderer, ag-grid's click handler would have
 * already run.
 *
 * We could also attempt to simply attach our own, native, click handler to
 * a wrapper around the custom renderer (i.e. grab a ref to the wrapper and
 * call `addEventListener`) and call `stopPropagation`. Then, React will never
 * get the click event because its native handler is all the way at the top -
 * so our dropdown doesn't open, although neither do we see the unwanted modal from
 * onRowClicked.
 *
 * Calling `e.stopPropagation()` inside a *React* onClick callback in custom renderer
 * would have worked only if ag-grid was actually using proper React onClick callbacks
 * instead of attaching event listeners directly to the DOM. Synthetic event's stopPropagation
 * is only equipped to "emulate" bubbling management of *virtual* events dispatched by React's
 * event handler which is actually on the top-level document element.
 *
 * Calling `e.nativeEvent.stopPropagation()` inside a React callback also does not work because
 * ag-grid's handler is called too early, before the event bubbles up to React.
 *
 * This is where `stopPropagationForAgGrid` becomes helpful (see
 * node_modules/@ag-grid-community/core/dist/es6/utils/event.js)
 * That function attaches an extra boolean flag to an event. Ag-Grid's event
 * handlers check for that flag and return if it is set without running the rest of
 * their logic (just search for `isStopPropagationForAgGrid` in `node_modules/@ag-grid-community`
 * to see quite a few of those checks).
 *
 * With this function, the sequence of steps that happen when a dropdown is clicked
 * is as follows:
 *
 * 1. Dropdown is clicked
 * 2. Click event bubbles up to this wrapper component, and we set stopPropagationForAgGrid flag
 * 3. Click event continues to bubble and reaches ag-grid's handler
 * 4. Ag-Grid's handler sees the flag and returns early, so our onRowClicked logic never runs
 * 5. Click event bubbles up all the way to document
 * 6. React's top-level handler gets the event and dispatches the synthetic event
 * 7. Dropdown component now reacts to the click and start opening the options list.
 *
 * See also:
 * - https://github.com/deckardtech/rentalscape/pull/982#discussion_r835650222
 * (more on how to find corresponding Ag-Grid components to track down event handler creation)
 *
 * - https://github.com/facebook/react/blob/3b96650e39ddda5ba49245713ef16dbc52d25e9e/src/renderers/dom/client/ReactBrowserEventEmitter.js#L23
 * - https://stackoverflow.com/questions/24415631/reactjs-syntheticevent-stoppropagation-only-works-with-react-events
 * (on React's synthetic events and the various `stopPropagation` methods)
 *
 * @param props Most of the props are passed through to the wrapping container element,
 * in particular `className` and `css`, making it possible to style the wrapper with
 * `styled-components`
 *
 * @param {keyof JSX.IntrinsicElements} props.as A string that specifies one of the
 * intrinsic React elements (e.g. 'div', 'span', etc). Use this to render the container
 * as a different HTML element.
 * @param {boolean} props.preventAgGridEventBubbling Whether ag-grid should be asked to
 * ignore click events bubbling up from this component and its children. Only click events
 * are handled at the moment.
 * @returns {JSX.Element}
 * @constructor
 */
export const InteractiveCellContainer = (props) => {
  const {
    children,
    as: WrapperComponent = 'div',
    preventAgGridEventBubbling = true,
    ...passthroughProps
  } = props;

  const containerRef = useRef();

  const onHandleBubbledClick = useCallback((e) => {
    stopPropagationForAgGrid(e);
  }, []);

  useEffect(() => {
    if (preventAgGridEventBubbling) {
      const wrapperDOMElement = containerRef.current;
      wrapperDOMElement?.addEventListener(
        EVENT_TYPE_CLICK,
        onHandleBubbledClick
      );
      return () => {
        wrapperDOMElement?.removeEventListener(
          EVENT_TYPE_CLICK,
          onHandleBubbledClick
        );
      };
    }

    return () => {};
  }, [preventAgGridEventBubbling, onHandleBubbledClick]);

  return (
    <WrapperComponent ref={containerRef} {...passthroughProps}>
      {children}
    </WrapperComponent>
  );
};
